import React, { useCallback } from 'react';

import { Linking } from 'react-native';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { LANGUAGE_KEYS } from 'config/languageKeys';
import {
    CLOSE_BUTTON_ID,
    FORGOT_PASSWORD_DESCRIPTION_ID,
    FORGOT_PASSWORD_TITLE_ID,
    SELFCARE_BUTTON_ID,
} from 'config/testID';

import { selectLayoutConfig } from 'store/config/selectors';

import { isTablet, isTabletOrWeb, isWeb } from 'utils/responsiveness';

import styles from 'screens/Login/ForgotPasswordDialog/styles';

import DialogView from 'components/Dialog';
import dialogStyles from 'components/Dialog/style';
import LocalizedText from 'components/LocalizedText';
import Text, { ETextCategory, ETextColor } from 'components/Text';

const {
    CLOSE_BUTTON,
    SCREEN_FORGOT_PASSWORD: {
        SCREEN_FORGOT_PASSWORD_TITLE,
        SCREEN_FORGOT_PASSWORD_BODY_1,
        SCREEN_FORGOT_PASSWORD_WEBSITE_BUTTON,
    },
    ACCESSIBILITY: {
        LOGIN: {
            ACCESSIBILITY_CLOSE_BUTTON,
            ACCESSIBILITY_LOGIN_SELFCARE_BUTTON,
        },
    },
} = LANGUAGE_KEYS;

type Props = {
    visible: boolean;
    closeDialog: () => void;
};

export default function ForgotPasswordDialog({ visible, closeDialog }: Props) {
    const { t } = useTranslation();

    const { background, primary } = useSelector(selectLayoutConfig);

    const handleOpenForgotPasswordWebsite = useCallback(() => {
        Linking.openURL(
            'https://www.one.hu/ugyintezes/hasznos/online-fiokok#segitseg',
        )
            .then(closeDialog)
            .catch(closeDialog);
    }, [closeDialog]);

    return (
        <DialogView
            style={dialogStyles.nonTransparentDialog}
            visible={visible}
            animationType="fade"
            title={SCREEN_FORGOT_PASSWORD_TITLE}
            titleTestID={FORGOT_PASSWORD_TITLE_ID}
            DescriptionComponent={() => (
                <Text
                    accessible
                    accessibilityLabel={t(SCREEN_FORGOT_PASSWORD_BODY_1)}
                    category={ETextCategory.body1}
                    color={ETextColor.light_grey}
                    style={styles.description}
                    numberOfLines={isTablet ? 3 : 4}
                    testID={FORGOT_PASSWORD_DESCRIPTION_ID}
                    {...(isWeb && { tabIndex: 0 })}
                >
                    <LocalizedText textKey={SCREEN_FORGOT_PASSWORD_BODY_1} />{' '}
                </Text>
            )}
            cancelButtonTextStyle={{ color: background }}
            cancelButtonAccessibilityLabel={t(ACCESSIBILITY_CLOSE_BUTTON)}
            revertButtons
            actionButtonTitle={SCREEN_FORGOT_PASSWORD_WEBSITE_BUTTON}
            actionButtonOnPress={handleOpenForgotPasswordWebsite}
            actionButtonTestID={SELFCARE_BUTTON_ID}
            closeButtonTestID={CLOSE_BUTTON_ID}
            actionButtonStyle={isTabletOrWeb && styles.selfCareButton}
            actionButtonAccessibilityLabel={t(
                ACCESSIBILITY_LOGIN_SELFCARE_BUTTON,
            )}
            actionButtonBordered
            cancelButtonTitle={CLOSE_BUTTON}
            cancelButtonOnPress={closeDialog}
            cancelButtonFillColor={primary}
        />
    );
}
